import { Container } from "./style";
import { useAuth } from '../../context/AuthContext';
import { useRef, useState } from "react";
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import useHandleErrors from "../../hooks/useHandleErrors";
import server from "../../global/server";
import AccountInfo from "./Forms/AccountInfo";
import ExtraInfo from "./Forms/ExtraInfo";
import PaymentInfo from "./Forms/PaymentInfo";
import InputFileWithCrop from "../../components/InputFileWithCrop";
import { Avatar, Box, Typography, IconButton, Tooltip, Chip, Button } from '@mui/material';
import AddAPhotoIcon from '@mui/icons-material/AddAPhoto';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LogoutIcon from '@mui/icons-material/Logout';
import { useConfirm } from 'material-ui-confirm';
import { SectionCard, SectionTitle } from "./Forms/Formstyle";

const MyProfile = () => {
    const navigate = useNavigate();
    const { user, verifySession, LogOut } = useAuth();
    const [hasError, setError] = useState();
    useHandleErrors(hasError);

    const { enqueueSnackbar } = useSnackbar();
    const confirm = useConfirm();
    const inputRef = useRef();
    const [previewImage, setPreviewImage] = useState(null);
    const [revoking, setRevoking] = useState(false);

    const saveData = async (values) => {
        try {
            await server.put('/account', values);
            await verifySession();
            enqueueSnackbar('Dados atualizados com sucesso!');
        } catch (error) {
            setError(error);
        }
    };

    const handleImageChange = async (e) => {
        setPreviewImage(e.preview);
        try {
            await server.put('/account', { profile_image: e.base64, email: user.email, name: user.name });
            await verifySession();
            enqueueSnackbar('Foto atualizada!');
        } catch (error) {
            setError(error);
            setPreviewImage(null);
        }
    };

    // Encerra todas as sessoes, inclusive a atual: o objetivo e expulsar quem
    // estiver em outro aparelho, entao nao faz sentido manter alguma viva.
    const handleRevokeSessions = async () => {
        try {
            await confirm({
                title: 'Deslogar de todos os dispositivos?',
                description:
                    'Você será desconectado em todos os aparelhos, incluindo este, e precisará entrar novamente.',
            });
        } catch { return; }

        try {
            setRevoking(true);
            await server.delete('/account/sessions');
            await LogOut();
            enqueueSnackbar('Você saiu de todos os dispositivos.', { variant: 'success' });
            navigate('/login');
        } catch (error) {
            setRevoking(false);
            setError(error);
        }
    };

    const isHost = Boolean(user?.profile);
    const isApproved = user?.profile?.approved;

    return (
        <Container>
            {/* Profile Header */}
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1.5,
                pt: 1,
            }}>
                <Box sx={{ position: 'relative' }}>
                    <Avatar
                        src={previewImage || user?.profileImage}
                        alt={user?.name}
                        sx={{
                            width: 100,
                            height: 100,
                            border: '3px solid #e0e0e0',
                            fontSize: '2.5rem',
                        }}
                    >
                        {user?.name?.[0]}
                    </Avatar>
                    <Tooltip title="Alterar foto">
                        <IconButton
                            onClick={() => inputRef.current?.click()}
                            size="small"
                            sx={{
                                position: 'absolute',
                                bottom: -2,
                                right: -2,
                                bgcolor: '#006a61',
                                color: 'white',
                                width: 32,
                                height: 32,
                                '&:hover': { bgcolor: '#005a52' },
                            }}
                        >
                            <AddAPhotoIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                    <InputFileWithCrop
                        inputRef={inputRef}
                        onChange={handleImageChange}
                        name="image"
                    />
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="h6" fontWeight={600} sx={{ lineHeight: 1.3 }}>
                        {user?.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        @{user?.username}
                    </Typography>
                </Box>
                {isHost && (
                    <Chip
                        icon={<CheckCircleIcon sx={{ fontSize: 16 }} />}
                        label={isApproved ? "Anfitrião aprovado" : "Anfitrião pendente"}
                        size="small"
                        sx={{
                            bgcolor: isApproved ? '#e8f5e9' : '#fff3e0',
                            color: isApproved ? '#2e7d32' : '#e65100',
                            fontWeight: 500,
                            '& .MuiChip-icon': { color: 'inherit' },
                        }}
                    />
                )}
            </Box>

            {/* Dados Pessoais */}
            <AccountInfo user={user} submit={saveData} />

            {/* Dados Profissionais */}
            <ExtraInfo user={user} submit={saveData} />

            {/* CTA Tornar-se Anfitriao */}
            {!isHost && (
                <SectionCard
                    as="button"
                    onClick={() => navigate('/perfil?sign=encontrado')}
                    style={{
                        cursor: 'pointer',
                        background: 'linear-gradient(135deg, #006a61 0%, #00897b 100%)',
                        border: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        textAlign: 'left',
                    }}
                >
                    <Box>
                        <Typography variant="subtitle1" fontWeight={600} color="white">
                            Tornar-se anfitrião
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', mt: 0.5 }}>
                            Compartilhe seu conhecimento e receba por isso
                        </Typography>
                    </Box>
                    <ArrowForwardIcon sx={{ color: 'white', fontSize: 28, flexShrink: 0, ml: 2 }} />
                </SectionCard>
            )}

            {/* Dados de Recebimento (apenas anfitrioes) */}
            {isHost && <PaymentInfo user={user} />}

            {/* Seguranca */}
            <SectionCard>
                <SectionTitle>Segurança</SectionTitle>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5, lineHeight: 1.6 }}>
                    Esqueceu a conta aberta em outro computador ou celular? Encerre todas as
                    sessões ativas de uma vez. Você precisará entrar novamente neste aparelho.
                </Typography>
                <Button
                    fullWidth
                    variant="outlined"
                    color="error"
                    disabled={revoking}
                    startIcon={<LogoutIcon />}
                    onClick={handleRevokeSessions}
                    sx={{
                        borderRadius: '8px',
                        height: 44,
                        textTransform: 'none',
                        fontWeight: 500,
                    }}
                >
                    {revoking ? 'Encerrando...' : 'Deslogar de todos os dispositivos'}
                </Button>
            </SectionCard>
        </Container>
    );
};

export default MyProfile;
